import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Plus, Loader2, Send, Download, Check, Edit3, Trash2, CheckCircle, AlertCircle, Bell } from 'lucide-react';
import { useLang } from '../../i18n';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { StatusBadge, LoadingSpinner } from '../common/Badges';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AdminInvoices = () => {
  const { t } = useLang();
  const [invoices, setInvoices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', address: '', email: '', phone: '',
    job_id: '',
    project_description: '',
    items: [{ description: '', quantity: 1, unit_price: '', amount: '' }],
    notes: '',
    payment_terms: 'net_30', due_date: '',
    apply_tax: true,
    tax_rate: 0.08875,
    manual_subtotal: '',   // Optional override - when set, uses this instead of computed sum
  });
  const [clientSig, setClientSig] = useState('');
  const [companySig, setCompanySig] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);
  const invoiceRef = useRef(null);

  const runReminders = async () => {
    setSendingReminders(true);
    try {
      const r = await api('/api/admin/invoices/run-reminders', { method: 'POST' });
      toast.success(r.message);
    } catch (e) { toast.error(e.message); }
    finally { setSendingReminders(false); }
  };

  useEffect(() => { loadData(); api('/api/customers').then(setCustomers).catch(() => {}); }, []);

  const loadData = async () => {
    try {
      const [invoicesData, requestsData] = await Promise.all([
        api('/api/admin/invoices'),
        api('/api/service-requests')
      ]);
      setInvoices(invoicesData);
      setRequests(requestsData);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, unit_price: '', amount: '' }] });
  const removeItem = (i) => setFormData({ ...formData, items: formData.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, f, v) => {
    const ni = [...formData.items];
    ni[i][f] = v;
    // Auto-compute amount when qty * unit_price are set, or unit_price when amount is edited
    if (f === 'quantity' || f === 'unit_price') {
      const qty = parseFloat(ni[i].quantity) || 0;
      const up = parseFloat(ni[i].unit_price) || 0;
      if (qty && up) ni[i].amount = (qty * up).toFixed(2);
    }
    setFormData({ ...formData, items: ni });
  };
  const computedSubtotal = formData.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  // If admin typed a manual override, use that; otherwise use the computed sum
  const subtotal = formData.manual_subtotal !== '' && formData.manual_subtotal !== null && !isNaN(parseFloat(formData.manual_subtotal))
    ? parseFloat(formData.manual_subtotal)
    : computedSubtotal;
  const taxRate = parseFloat(formData.tax_rate) || 0;
  const nycTax = formData.apply_tax ? subtotal * taxRate : 0;
  const total = subtotal + nycTax;

  const prefillFromJob = (jobId) => {
    const req = requests.find(r => r.id === jobId);
    if (req) {
      setFormData(prev => ({
        ...prev, job_id: jobId,
        first_name: req.customer_name?.split(' ')[0] || '',
        last_name: req.customer_name?.split(' ').slice(1).join(' ') || '',
        address: req.address || '',
        email: req.customer_email || '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const items = formData.items.map(i => ({
        description: i.description,
        quantity: parseFloat(i.quantity) || 1,
        unit_price: parseFloat(i.unit_price) || (parseFloat(i.amount) || 0),
        amount: parseFloat(i.amount) || 0,
      }));
      const payload = {
        ...formData,
        items,
        tax_rate: formData.apply_tax ? (parseFloat(formData.tax_rate) || 0) : 0,
        apply_tax: !!formData.apply_tax,
        manual_subtotal: formData.manual_subtotal !== '' ? parseFloat(formData.manual_subtotal) : null,
        client_signature: clientSig, company_signature: companySig
      };
      await api('/api/invoices/custom', { method: 'POST', body: JSON.stringify(payload) });
      toast.success('Invoice created!');
      setShowForm(false);
      setFormData({ first_name: '', last_name: '', address: '', email: '', phone: '', job_id: '', project_description: '', items: [{ description: '', quantity: 1, unit_price: '', amount: '' }], notes: '', payment_terms: 'net_30', due_date: '', apply_tax: true, tax_rate: 0.08875, manual_subtotal: '' });
      setClientSig(''); setCompanySig('');
      loadData();
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  const downloadPDF = async (invoiceId) => {
    try {
      // Use /detail endpoint - it returns logo_url + customer_address + ticket_number
      const data = await api(`/api/invoices/${invoiceId}/detail`);
      setShowPreview(data);
      setTimeout(async () => {
        const el = document.getElementById('invoice-preview-pdf');
        if (!el) return;
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const imgH = (canvas.height * pageW) / canvas.width;
        // Multi-page support so long invoices don't get clipped
        let heightLeft = imgH;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
        heightLeft -= pageH;
        while (heightLeft > 0) {
          position = heightLeft - imgH;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
          heightLeft -= pageH;
        }
        pdf.save(`BELLAJA-Invoice-${data.invoice_number}.pdf`);
        toast.success('PDF downloaded!');
        setShowPreview(null);
      }, 700);
    } catch { toast.error('Failed to generate PDF'); }
  };

  const sendInvoice = async (id) => {
    try { await api(`/api/invoices/${id}/send`, { method: 'PUT' }); toast.success('Fatura u dërgua!'); loadData(); }
    catch (err) { toast.error(err.message); }
  };

  const deleteInvoice = async (id, num) => {
    if (!window.confirm(`A jeni i sigurt që doni të fshini faturën "${num}"?`)) return;
    try { await api(`/api/invoices/${id}`, { method: 'DELETE' }); toast.success('Fatura u fshi!'); loadData(); }
    catch (err) { toast.error(err.message); }
  };

  const openEdit = async (id) => {
    try {
      const inv = await api(`/api/invoices/${id}`);
      setEditing({
        id,
        invoice_number: inv.invoice_number,
        assign_customer_id: inv.customer_id || '',
        _orig_customer_id: inv.customer_id || '',
        customer_name: inv.customer_name || '',
        customer_email: inv.customer_email || '',
        customer_phone: inv.customer_phone || '',
        customer_address: inv.customer_address || '',
        items: inv.items?.length
          ? inv.items.map((i, idx) => ({ _uid: `inv-${id}-${idx}-${i.description || ''}`, description: i.description, amount: i.amount }))
          : [{ _uid: `inv-new-${Date.now()}`, description: '', amount: 0 }],
        notes: inv.notes || '',
        payment_terms: inv.payment_terms || 'net_30',
        due_date: inv.due_date || '',
        status: inv.status || 'draft',
      });
    } catch (err) { toast.error(err.message); }
  };

  const saveInvoiceEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        customer_name: editing.customer_name,
        customer_email: editing.customer_email,
        customer_phone: editing.customer_phone,
        customer_address: editing.customer_address,
        items: editing.items.map(i => ({ description: i.description, amount: parseFloat(i.amount) || 0 })),
        notes: editing.notes,
        payment_terms: editing.payment_terms,
        due_date: editing.due_date,
        status: editing.status,
      };
      await api(`/api/invoices/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      if (editing.assign_customer_id && editing.assign_customer_id !== editing._orig_customer_id) {
        await api(`/api/admin/invoices/${editing.id}/assign-customer`, {
          method: 'PUT', body: JSON.stringify({ customer_id: editing.assign_customer_id })
        });
      }
      toast.success('Invoice updated');
      setEditing(null);
      loadData();
    } catch (err) { toast.error(err.message); }
    finally { setSavingEdit(false); }
  };

  const editItemField = (idx, field, value) => {
    setEditing(prev => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, [field]: value } : it) }));
  };
  const editAddItem = () => setEditing(p => ({ ...p, items: [...p.items, { _uid: `inv-new-${Date.now()}-${Math.random()}`, description: '', amount: 0 }] }));
  const editRemoveItem = (idx) => setEditing(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="admin-invoices" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">{t('billing')}</p>
          <h1 className="font-heading text-3xl font-bold text-white">{t('invoices_title')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runReminders} disabled={sendingReminders} className="flex items-center gap-2 px-4 py-2.5 border border-gray-700 rounded-lg text-gray-300 text-sm font-bold hover:border-red-500 disabled:opacity-50" data-testid="run-reminders-btn">
            {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Payment Reminders
          </button>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4" />{t('create_invoice')}
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800/50"><tr>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('invoice_num')}</th>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('customer').toUpperCase()}</th>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('amount')}</th>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('status').toUpperCase()}</th>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('signed_col').toUpperCase()}</th>
            <th className="px-5 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('actions').toUpperCase()}</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-800/50">
            {invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-gray-800/30" data-testid={`admin-invoice-row-${inv.id}`}>
                <td className="px-5 py-3 font-mono text-sm text-white">{inv.invoice_number}</td>
                <td className="px-5 py-3 text-sm text-gray-300">{inv.customer_name}</td>
                <td className="px-5 py-3 text-sm font-bold text-green-400">${inv.total.toFixed(2)}</td>
                <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-5 py-3" data-testid={`invoice-signed-${inv.invoice_number}`}>
                  {inv.client_signature ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-900/40 text-green-400 text-xs font-semibold">✓ {t('signed_col')}</span>
                  ) : (
                    <span className="text-gray-600 text-xs">—</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(inv.id)} className="text-xs bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid={`edit-invoice-${inv.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />{t('edit')}
                    </button>
                    <button onClick={() => downloadPDF(inv.id)} className="text-xs bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid={`download-invoice-${inv.id}`}>
                      <Download className="w-3.5 h-3.5" />{t('download_pdf')}
                    </button>
                    {inv.status === 'draft' && (
                      <button onClick={() => sendInvoice(inv.id)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid={`send-invoice-btn-${inv.id}`}>
                        <Send className="w-3.5 h-3.5" />{t('send_invoice')}
                      </button>
                    )}
                    <button onClick={() => deleteInvoice(inv.id, inv.invoice_number)} className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-2.5 py-1.5 rounded font-medium" data-testid={`delete-invoice-${inv.id}`}>
                      {t('delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoices.length === 0 && <div className="p-10 text-center"><FileText className="w-8 h-8 mx-auto mb-2 text-gray-700" /><p className="text-sm text-gray-600">{t('no_invoices')}</p></div>}
      </div>

      {/* Create Invoice Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full my-8 overflow-hidden" data-testid="create-invoice-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-900">
              <div>
                <p className="text-xs text-red-500 font-bold tracking-widest">NEW INVOICE</p>
                <h3 className="text-lg font-bold text-white">Create Professional Invoice</h3>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-gray-800"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Link to Job (optional) */}
              <div>
                <label className="block text-xs text-gray-500 mb-1 tracking-wider">LINK TO JOB (OPTIONAL)</label>
                <select className="input" value={formData.job_id} onChange={(e) => { setFormData({ ...formData, job_id: e.target.value }); if (e.target.value) prefillFromJob(e.target.value); }} data-testid="invoice-job-select">
                  <option value="">Select job to prefill...</option>
                  {requests.map(r => <option key={r.id} value={r.id}>{r.ticket_number} - {r.customer_name}</option>)}
                </select>
              </div>

              {/* Client Details */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-800 space-y-3">
                <p className="text-xs text-red-500 font-bold tracking-widest">CLIENT DETAILS</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">First Name</label>
                    <input className="input" placeholder="First Name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required data-testid="inv-first-name" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                    <input className="input" placeholder="Last Name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required data-testid="inv-last-name" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Address</label>
                  <input className="input" placeholder="Full Address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required data-testid="inv-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input type="email" className="input" placeholder="client@email.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="inv-email" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                    <input className="input" placeholder="(xxx) xxx-xxxx" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="inv-phone" />
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <div>
                <label className="block text-xs text-red-500 font-bold tracking-widest mb-1">PROJECT DESCRIPTION</label>
                <textarea
                  className="input min-h-[80px]"
                  placeholder="Brief overview of the work performed - e.g. Full panel upgrade to 200A service, new 20A circuits for kitchen, EV charger installation..."
                  value={formData.project_description}
                  onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                  data-testid="project-description-input"
                />
              </div>

              {/* Line Items */}
              <div>
                <p className="text-xs text-red-500 font-bold tracking-widest mb-3">LINE ITEMS</p>
                <div className="grid grid-cols-[1fr_70px_100px_100px_32px] gap-2 mb-2">
                  <p className="text-xs text-gray-500 pl-1">Description</p>
                  <p className="text-xs text-gray-500 text-right">Qty</p>
                  <p className="text-xs text-gray-500 text-right">Unit Price ($)</p>
                  <p className="text-xs text-gray-500 text-right pr-1">Amount ($)</p>
                  <div></div>
                </div>
                {formData.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-[1fr_70px_100px_100px_32px] gap-2 mb-2 items-center">
                    <input className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2.5 text-white text-sm focus:border-red-500 focus:outline-none" placeholder="e.g. Panel Upgrade 200A with permit" value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} required data-testid={`item-desc-${i}`} />
                    <input type="number" step="0.01" min="0" className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2.5 text-white text-sm text-right focus:border-red-500 focus:outline-none" placeholder="1" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} data-testid={`item-qty-${i}`} />
                    <input type="number" step="0.01" className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2.5 text-white text-sm text-right focus:border-red-500 focus:outline-none" placeholder="0.00" value={item.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} data-testid={`item-unit-${i}`} />
                    <input type="number" step="0.01" className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-2.5 text-white text-sm text-right focus:border-red-500 focus:outline-none" placeholder="0.00" value={item.amount} onChange={(e) => updateItem(i, 'amount', e.target.value)} required data-testid={`item-amount-${i}`} />
                    {formData.items.length > 1 ? (
                      <button type="button" onClick={() => removeItem(i)} className="w-8 h-10 flex items-center justify-center text-red-500 hover:bg-red-500/10 rounded"><X className="w-4 h-4" /></button>
                    ) : <div className="w-8"></div>}
                  </div>
                ))}
                <button type="button" onClick={addItem} className="text-sm text-red-500 hover:text-red-400 flex items-center gap-1 mt-2" data-testid="add-item-btn"><Plus className="w-3.5 h-3.5" />Add Item</button>

                <div className="mt-4 pt-4 border-t border-gray-800 space-y-2">
                  {/* Subtotal - editable override */}
                  <div className="flex justify-end items-center gap-3 text-sm">
                    <span className="text-gray-400">Subtotal:</span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={computedSubtotal.toFixed(2)}
                        value={formData.manual_subtotal}
                        onChange={(e) => setFormData({ ...formData, manual_subtotal: e.target.value })}
                        className="w-32 bg-gray-800 border border-gray-700 rounded pl-6 pr-2 py-1.5 text-white text-sm text-right font-mono focus:border-red-500 focus:outline-none"
                        data-testid="manual-subtotal-input"
                        title="Override the auto-computed subtotal. Leave empty to use sum of line items."
                      />
                    </div>
                    {formData.manual_subtotal !== '' && (
                      <button type="button" onClick={() => setFormData({ ...formData, manual_subtotal: '' })} className="text-xs text-red-400 hover:text-red-300" title="Reset to auto-computed">×</button>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-600 text-right -mt-1.5">
                    {formData.manual_subtotal !== '' ? `Override (auto: $${computedSubtotal.toFixed(2)})` : 'Auto-computed from line items'}
                  </p>

                  {/* NYC Tax - editable rate + on/off toggle */}
                  <div className="flex justify-end items-center gap-3 text-sm">
                    <label className="flex items-center gap-2 text-gray-400 cursor-pointer" title="Toggle tax on/off">
                      <input
                        type="checkbox"
                        checked={formData.apply_tax}
                        onChange={(e) => setFormData({ ...formData, apply_tax: e.target.checked })}
                        className="w-4 h-4 accent-red-500"
                        data-testid="apply-tax-checkbox"
                      />
                      Sales Tax:
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.001"
                        disabled={!formData.apply_tax}
                        value={(parseFloat(formData.tax_rate) * 100).toFixed(3).replace(/\.?0+$/, '')}
                        onChange={(e) => setFormData({ ...formData, tax_rate: (parseFloat(e.target.value) || 0) / 100 })}
                        className="w-20 bg-gray-800 border border-gray-700 rounded pl-2 pr-6 py-1.5 text-white text-sm text-right font-mono focus:border-red-500 focus:outline-none disabled:opacity-40"
                        data-testid="tax-rate-input"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                    </div>
                    <span className={`text-white font-mono w-28 text-right ${!formData.apply_tax ? 'opacity-40 line-through' : ''}`}>${nycTax.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-end items-center gap-3 pt-2 border-t border-gray-700">
                    <span className="text-base font-bold text-white">TOTAL:</span>
                    <span className="text-2xl font-bold text-green-400 font-mono w-32 text-right">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Terms & Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1 tracking-wider">PAYMENT TERMS</label>
                  <select className="input" value={formData.payment_terms} onChange={(e) => {
                    const terms = e.target.value;
                    setFormData(prev => {
                      const days = terms === 'net_15' ? 15 : terms === 'net_30' ? 30 : terms === 'net_60' ? 60 : 0;
                      const due = days > 0 ? new Date(Date.now() + days * 86400000).toISOString().split('T')[0] : prev.due_date;
                      return { ...prev, payment_terms: terms, due_date: due };
                    });
                  }} data-testid="payment-terms-select">
                    <option value="due_on_receipt">Due on Receipt</option>
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_60">Net 60</option>
                    <option value="custom">Custom Date</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1 tracking-wider">DUE DATE</label>
                  <input type="date" className="input" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="due-date-input" />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs text-gray-500 mb-1 tracking-wider">NOTES</label>
                <textarea className="input min-h-[60px]" placeholder="Payment terms, special instructions..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="invoice-notes-input" />
              </div>

              {/* Signatures */}
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-800">
                <p className="text-xs text-red-500 font-bold tracking-widest mb-3">SIGNATURES</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Client Signature</label>
                    <input className="input text-center italic" placeholder="Client full name" value={clientSig} onChange={(e) => setClientSig(e.target.value)} data-testid="client-signature" />
                    <div className="mt-1 border-t-2 border-gray-600 pt-1"><p className="text-[10px] text-gray-600 text-center">Client Signature</p></div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Company Signature</label>
                    <input className="input text-center italic" placeholder="Authorized representative" value={companySig} onChange={(e) => setCompanySig(e.target.value)} data-testid="company-signature" />
                    <div className="mt-1 border-t-2 border-gray-600 pt-1"><p className="text-[10px] text-gray-600 text-center">BELLAJA Electrical</p></div>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full py-3 font-bold text-lg" data-testid="submit-invoice-btn">
                {submitting && <Loader2 className="w-5 h-5 animate-spin inline mr-2" />}
                Create Invoice
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Invoice PDF Preview (hidden, used for PDF generation) */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
          <div className="bg-white max-w-[210mm] w-full max-h-[90vh] overflow-auto rounded-lg">
            <div id="invoice-preview-pdf" className="p-12" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: '#1a1a1a', background: '#fff', width: '210mm', boxSizing: 'border-box' }}>

              {/* === HEADER === */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '18px' }}>
                  {showPreview.logo_url ? (
                    <img
                      src={showPreview.logo_url.startsWith('http') ? showPreview.logo_url : `${process.env.REACT_APP_BACKEND_URL}${showPreview.logo_url}`}
                      alt="Logo"
                      style={{ width: '78px', height: '78px', objectFit: 'contain', borderRadius: '8px' }}
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div style={{ width: '78px', height: '78px', background: '#E31B23', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '36px', fontWeight: 800 }}>B</div>
                  )}
                  <div>
                    <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0a0a0a', margin: 0, letterSpacing: '0.5px' }}>BELLAJA</h1>
                    <p style={{ fontSize: '11px', color: '#666', margin: '2px 0 8px', letterSpacing: '0.5px', fontWeight: 600 }}>ELECTRICAL & SECURITY SYSTEMS</p>
                    <p style={{ fontSize: '10.5px', color: '#444', margin: '1px 0' }}>{showPreview.company?.address || '135-20 40th Rd, Ste 105, Flushing, NY 11354'}</p>
                    <p style={{ fontSize: '10.5px', color: '#444', margin: '1px 0' }}>
                      {showPreview.company?.phone || '(347) 446-5498'} · {showPreview.company?.email || 'info@bellajaelectric.com'}
                    </p>
                    <p style={{ fontSize: '10.5px', color: '#E31B23', margin: '1px 0', fontWeight: 600 }}>www.bellajaelectric.com</p>
                  </div>
                </div>

                <div style={{ textAlign: 'right', position: 'relative' }}>
                  <h2 style={{ fontSize: '40px', fontWeight: 800, color: '#0a0a0a', margin: 0, letterSpacing: '1px' }}>INVOICE</h2>
                  <p style={{ fontSize: '14px', color: '#E31B23', fontWeight: 700, margin: '4px 0 12px', fontFamily: 'monospace' }}>{showPreview.invoice_number}</p>
                  <table style={{ marginLeft: 'auto', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr><td style={{ color: '#888', paddingRight: '14px', textAlign: 'right' }}>Issued:</td><td style={{ color: '#222', fontWeight: 600, textAlign: 'right' }}>{new Date(showPreview.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td></tr>
                      {showPreview.due_date && <tr><td style={{ color: '#888', paddingRight: '14px', textAlign: 'right' }}>Due:</td><td style={{ color: '#222', fontWeight: 600, textAlign: 'right' }}>{new Date(showPreview.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td></tr>}
                      {showPreview.payment_terms && <tr><td style={{ color: '#888', paddingRight: '14px', textAlign: 'right' }}>Terms:</td><td style={{ color: '#222', fontWeight: 600, textAlign: 'right' }}>{showPreview.payment_terms.replace(/_/g, ' ').toUpperCase()}</td></tr>}
                      {showPreview.ticket_number && <tr><td style={{ color: '#888', paddingRight: '14px', textAlign: 'right' }}>Ticket:</td><td style={{ color: '#222', fontWeight: 600, fontFamily: 'monospace', textAlign: 'right' }}>{showPreview.ticket_number}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Status stamp */}
              {showPreview.status && (
                <div style={{ position: 'absolute', top: '120px', right: '60px', transform: 'rotate(-12deg)', border: `3px solid ${showPreview.status === 'paid' ? '#16a34a' : showPreview.status === 'overdue' ? '#dc2626' : '#f59e0b'}`, color: showPreview.status === 'paid' ? '#16a34a' : showPreview.status === 'overdue' ? '#dc2626' : '#f59e0b', padding: '6px 18px', fontSize: '20px', fontWeight: 800, letterSpacing: '3px', borderRadius: '6px', opacity: 0.85, fontFamily: 'monospace' }}>
                  {showPreview.status === 'paid' ? 'PAID' : showPreview.status === 'overdue' ? 'OVERDUE' : (showPreview.status === 'draft' ? 'DRAFT' : 'UNPAID')}
                </div>
              )}

              {/* Red bar separator */}
              <div style={{ height: '4px', background: 'linear-gradient(90deg, #E31B23 0%, #b91c1c 100%)', marginBottom: '24px', borderRadius: '2px' }}></div>

              {/* === BILL TO === */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                <div style={{ background: '#f7f7f8', padding: '16px 18px', borderRadius: '8px', borderLeft: '4px solid #E31B23' }}>
                  <p style={{ fontSize: '9px', color: '#999', letterSpacing: '2.5px', fontWeight: 700, margin: '0 0 8px' }}>BILL TO</p>
                  <p style={{ fontSize: '15px', fontWeight: 700, color: '#0a0a0a', margin: '0 0 4px' }}>{showPreview.customer_name}</p>
                  {showPreview.customer_address && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>{showPreview.customer_address}</p>}
                  {showPreview.customer_email && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>{showPreview.customer_email}</p>}
                  {showPreview.customer_phone && <p style={{ fontSize: '11px', color: '#555', margin: '2px 0' }}>{showPreview.customer_phone}</p>}
                </div>
                <div style={{ background: '#0a0a0a', color: '#fff', padding: '16px 18px', borderRadius: '8px' }}>
                  <p style={{ fontSize: '9px', color: '#E31B23', letterSpacing: '2.5px', fontWeight: 700, margin: '0 0 8px' }}>AMOUNT DUE</p>
                  <p style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', fontFamily: 'monospace' }}>
                    ${((showPreview.balance_due !== undefined ? showPreview.balance_due : showPreview.total) || 0).toFixed(2)}
                  </p>
                  {showPreview.amount_paid > 0 && <p style={{ fontSize: '10px', color: '#aaa', margin: 0 }}>Paid: <span style={{ color: '#4ade80', fontWeight: 600 }}>${showPreview.amount_paid.toFixed(2)}</span> of ${showPreview.total.toFixed(2)}</p>}
                </div>
              </div>

              {/* === PROJECT DESCRIPTION === */}
              {showPreview.project_description && (
                <div style={{ marginBottom: '20px', padding: '14px 18px', background: '#fffbf0', border: '1px solid #f0e6c0', borderRadius: '8px' }}>
                  <p style={{ fontSize: '9px', color: '#a16207', letterSpacing: '2px', fontWeight: 700, margin: '0 0 6px' }}>PROJECT DESCRIPTION</p>
                  <p style={{ fontSize: '12px', color: '#1a1a1a', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>{showPreview.project_description}</p>
                </div>
              )}

              {/* === ITEMS TABLE === */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#0a0a0a', color: '#fff' }}>
                    <th style={{ textAlign: 'left', padding: '12px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', borderTopLeftRadius: '6px' }}>DESCRIPTION</th>
                    <th style={{ textAlign: 'right', padding: '12px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', width: '60px' }}>QTY</th>
                    <th style={{ textAlign: 'right', padding: '12px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', width: '110px' }}>UNIT PRICE</th>
                    <th style={{ textAlign: 'right', padding: '12px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', width: '120px', borderTopRightRadius: '6px' }}>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {(showPreview.items || []).map((item, i) => {
                    const qty = item.quantity ?? 1;
                    const up = item.unit_price ?? (item.amount && qty ? item.amount / qty : item.amount);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '12px 14px', verticalAlign: 'top' }}>{item.description}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', verticalAlign: 'top', fontFamily: 'monospace' }}>{qty}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', verticalAlign: 'top', fontFamily: 'monospace' }}>${(up || 0).toFixed(2)}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', verticalAlign: 'top', fontWeight: 700, fontFamily: 'monospace' }}>${(item.amount || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* === TOTALS === */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
                <table style={{ minWidth: '320px', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '6px 14px', color: '#666', textAlign: 'right' }}>Subtotal:</td>
                      <td style={{ padding: '6px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>${(showPreview.subtotal || 0).toFixed(2)}</td>
                    </tr>
                    {showPreview.apply_tax !== false && (showPreview.tax_amount || 0) > 0 && (
                      <tr>
                        <td style={{ padding: '6px 14px', color: '#666', textAlign: 'right' }}>
                          NYC Sales Tax ({((showPreview.tax_rate || 0) * 100).toFixed(3).replace(/\.?0+$/, '')}%):
                        </td>
                        <td style={{ padding: '6px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>${(showPreview.tax_amount || 0).toFixed(2)}</td>
                      </tr>
                    )}
                    <tr style={{ borderTop: '2px solid #0a0a0a' }}>
                      <td style={{ padding: '10px 14px', color: '#0a0a0a', textAlign: 'right', fontWeight: 700, fontSize: '14px' }}>TOTAL:</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, fontSize: '16px', color: '#0a0a0a' }}>
                        ${(showPreview.total || 0).toFixed(2)}
                      </td>
                    </tr>
                    {showPreview.amount_paid > 0 && (
                      <>
                        <tr>
                          <td style={{ padding: '6px 14px', color: '#16a34a', textAlign: 'right' }}>Amount Paid:</td>
                          <td style={{ padding: '6px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#16a34a' }}>−${(showPreview.amount_paid || 0).toFixed(2)}</td>
                        </tr>
                        <tr style={{ borderTop: '1px solid #e5e5e5' }}>
                          <td style={{ padding: '8px 14px', color: '#E31B23', textAlign: 'right', fontWeight: 700 }}>Balance Due:</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, fontSize: '14px', color: '#E31B23' }}>${(showPreview.balance_due || 0).toFixed(2)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* === NOTES === */}
              {showPreview.notes && (
                <div style={{ marginBottom: '32px', padding: '14px 18px', background: '#f7f7f8', borderRadius: '8px', borderLeft: '3px solid #999' }}>
                  <p style={{ fontSize: '9px', color: '#666', letterSpacing: '2px', fontWeight: 700, margin: '0 0 6px' }}>NOTES</p>
                  <p style={{ fontSize: '11px', color: '#333', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>{showPreview.notes}</p>
                </div>
              )}

              {/* === SIGNATURES === */}
              {(showPreview.client_signature || showPreview.company_signature) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', gap: '32px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ borderBottom: '1.5px solid #0a0a0a', paddingBottom: '6px', minHeight: '34px' }}>
                      <p style={{ fontSize: '15px', fontStyle: 'italic', color: '#0a0a0a', margin: 0, fontFamily: '"Brush Script MT", cursive' }}>{showPreview.client_signature || ''}</p>
                    </div>
                    <p style={{ fontSize: '9px', color: '#888', marginTop: '4px', letterSpacing: '1.5px' }}>CLIENT SIGNATURE</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ borderBottom: '1.5px solid #0a0a0a', paddingBottom: '6px', minHeight: '34px' }}>
                      <p style={{ fontSize: '15px', fontStyle: 'italic', color: '#0a0a0a', margin: 0, fontFamily: '"Brush Script MT", cursive' }}>{showPreview.company_signature || ''}</p>
                    </div>
                    <p style={{ fontSize: '9px', color: '#888', marginTop: '4px', letterSpacing: '1.5px' }}>BELLAJA AUTHORIZED REPRESENTATIVE</p>
                  </div>
                </div>
              )}

              {/* === FOOTER === */}
              <div style={{ marginTop: '48px', paddingTop: '20px', borderTop: '3px double #E31B23' }}>
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <p style={{ fontSize: '12px', fontWeight: 700, color: '#0a0a0a', margin: '0 0 4px', letterSpacing: '1px' }}>THANK YOU FOR CHOOSING BELLAJA</p>
                  <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>
                    NYC DOB Licensed · Connecticut State Licensed · Fully Insured & Bonded · 1-Year Labor Warranty
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', padding: '10px 16px', background: '#0a0a0a', color: '#fff', borderRadius: '6px' }}>
                  <span style={{ fontSize: '10px', color: '#aaa' }}>Payment: Cash · Check · Wire · Stripe</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#E31B23' }}>www.bellajaelectric.com</span>
                  <span style={{ fontSize: '10px', color: '#aaa' }}>{showPreview.company?.phone || '(347) 446-5498'}</span>
                </div>
                <p style={{ textAlign: 'center', fontSize: '8px', color: '#bbb', marginTop: '10px' }}>This invoice was generated electronically. Late payments may incur a 1.5%/month finance charge as permitted by NY law.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full my-6" data-testid="edit-invoice-modal">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-red-500 font-bold tracking-widest">EDIT INVOICE</p>
                <h3 className="text-base font-bold text-white">{editing.invoice_number}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-800 rounded"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">ASSIGN TO CUSTOMER ACCOUNT</label>
                <select value={editing.assign_customer_id || ''} onChange={(e) => setEditing({ ...editing, assign_customer_id: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-assign-customer">
                  <option value="">— not linked to any account —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                </select>
                <p className="text-[10px] text-gray-600 mt-1">Only the selected customer will see & download this invoice in their portal once its status is not draft.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">CUSTOMER NAME</label>
                  <input value={editing.customer_name} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-name" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">EMAIL</label>
                  <input value={editing.customer_email} onChange={(e) => setEditing({ ...editing, customer_email: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-email" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">PHONE</label>
                  <input value={editing.customer_phone} onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-phone" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">STATUS</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-status">
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">ADDRESS</label>
                  <input value={editing.customer_address} onChange={(e) => setEditing({ ...editing, customer_address: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-address" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">PAYMENT TERMS</label>
                  <select value={editing.payment_terms} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm">
                    <option value="due_on_receipt">Due on Receipt</option>
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_60">Net 60</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">DUE DATE</label>
                  <input type="date" value={editing.due_date} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] text-red-500 font-bold tracking-widest">LINE ITEMS</p>
                  <button onClick={editAddItem} className="text-[10px] bg-gray-800 hover:bg-gray-700 text-white px-2 py-1 rounded flex items-center gap-1" data-testid="edit-add-item"><Plus className="w-3 h-3" />Add</button>
                </div>
                {editing.items.map((it, i) => (
                  <div key={it._uid || `item-${i}`} className="grid grid-cols-[1fr_120px_32px] gap-2 mb-2 items-center">
                    <input value={it.description} onChange={(e) => editItemField(i, 'description', e.target.value)} placeholder="Description" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid={`edit-item-desc-${i}`} />
                    <input type="number" step="0.01" value={it.amount} onChange={(e) => editItemField(i, 'amount', e.target.value)} placeholder="0.00" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm text-right" data-testid={`edit-item-amount-${i}`} />
                    {editing.items.length > 1 ? (
                      <button onClick={() => editRemoveItem(i)} className="p-2 bg-red-900/30 hover:bg-red-800/50 rounded" data-testid={`edit-remove-item-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    ) : <div />}
                  </div>
                ))}
                <p className="text-right text-sm font-bold text-green-400 mt-2">Total: ${editing.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(2)}</p>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-bold tracking-wider mb-1 block">NOTES</label>
                <textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" data-testid="edit-inv-notes" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-800 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={saveInvoiceEdit} disabled={savingEdit} className="btn-primary text-sm flex items-center gap-2" data-testid="save-inv-edit-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Save Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== PUBLIC GALLERY PAGE =====================


export default AdminInvoices;
